/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Api
 * Handoff - Knowledge Continuity Platform API
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  ActivityItem,
  ChatWithHandoverBody,
  ChatWithHandoverResponse,
  CreateKnowledgeUnitBody,
  CreateOnboardingTaskBody,
  CreateTransitionBody,
  DashboardSummary,
  Deliverable,
  ExpertiseSignal,
  HealthStatus,
  InterviewExtractBody,
  InterviewExtractResponse,
  InterviewSuggestBody,
  InterviewSuggestResponse,
  KnowledgeUnit,
  ListKnowledgeUnitsParams,
  ListTransitionsParams,
  OnboardingTask,
  PhaseCount,
  RoiCalculatorInput,
  RoiCalculatorResult,
  Transition,
  UpdateOnboardingTaskBody,
  UpdateTransitionBody,
} from "./api.schemas";

import { customFetch } from "../custom-fetch";
import type { ErrorType, BodyType } from "../custom-fetch";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * @summary Health check
 */
export const getHealthCheckUrl = () => {
  return `/api/healthz`;
};

export const healthCheck = async (
  options?: RequestInit,
): Promise<HealthStatus> => {
  return customFetch<HealthStatus>(getHealthCheckUrl(), {
    ...options,
    method: "GET",
  });
};

export const getHealthCheckQueryKey = () => {
  return [`/api/healthz`] as const;
};

export const getHealthCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthCheckQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof healthCheck>>> = ({
    signal,
  }) => healthCheck({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type HealthCheckQueryResult = NonNullable<
  Awaited<ReturnType<typeof healthCheck>>
>;
export type HealthCheckQueryError = ErrorType<unknown>;

/**
 * @summary Health check
 */

export function useHealthCheck<
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getHealthCheckQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List all transitions
 */
export const getListTransitionsUrl = (params?: ListTransitionsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/transitions?${stringifiedParams}`
    : `/api/transitions`;
};

export const listTransitions = async (
  params?: ListTransitionsParams,
  options?: RequestInit,
): Promise<Transition[]> => {
  return customFetch<Transition[]>(getListTransitionsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListTransitionsQueryKey = (params?: ListTransitionsParams) => {
  return [`/api/transitions`, ...(params ? [params] : [])] as const;
};

export const getListTransitionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listTransitions>>,
  TError = ErrorType<unknown>,
>(
  params?: ListTransitionsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listTransitions>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTransitionsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listTransitions>>> = ({
    signal,
  }) => listTransitions(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listTransitions>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListTransitionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listTransitions>>
>;
export type ListTransitionsQueryError = ErrorType<unknown>;

/**
 * @summary List all transitions
 */

export function useListTransitions<
  TData = Awaited<ReturnType<typeof listTransitions>>,
  TError = ErrorType<unknown>,
>(
  params?: ListTransitionsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listTransitions>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListTransitionsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create a new transition
 */
export const getCreateTransitionUrl = () => {
  return `/api/transitions`;
};

export const createTransition = async (
  createTransitionBody: CreateTransitionBody,
  options?: RequestInit,
): Promise<Transition> => {
  return customFetch<Transition>(getCreateTransitionUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createTransitionBody),
  });
};

export const getCreateTransitionMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTransition>>,
    TError,
    { data: BodyType<CreateTransitionBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createTransition>>,
  TError,
  { data: BodyType<CreateTransitionBody> },
  TContext
> => {
  const mutationKey = ["createTransition"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createTransition>>,
    { data: BodyType<CreateTransitionBody> }
  > = (props) => {
    const { data } = props ?? {};

    return createTransition(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTransitionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createTransition>>
>;
export type CreateTransitionMutationBody = BodyType<CreateTransitionBody>;
export type CreateTransitionMutationError = ErrorType<unknown>;

/**
 * @summary Create a new transition
 */
export const useCreateTransition = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTransition>>,
    TError,
    { data: BodyType<CreateTransitionBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createTransition>>,
  TError,
  { data: BodyType<CreateTransitionBody> },
  TContext
> => {
  return useMutation(getCreateTransitionMutationOptions(options));
};

/**
 * @summary Get a single transition
 */
export const getGetTransitionUrl = (id: number) => {
  return `/api/transitions/${id}`;
};

export const getTransition = async (
  id: number,
  options?: RequestInit,
): Promise<Transition> => {
  return customFetch<Transition>(getGetTransitionUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetTransitionQueryKey = (id: number) => {
  return [`/api/transitions/${id}`] as const;
};

export const getGetTransitionQueryOptions = <
  TData = Awaited<ReturnType<typeof getTransition>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTransition>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTransitionQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTransition>>> = ({
    signal,
  }) => getTransition(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getTransition>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetTransitionQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTransition>>
>;
export type GetTransitionQueryError = ErrorType<void>;

/**
 * @summary Get a single transition
 */

export function useGetTransition<
  TData = Awaited<ReturnType<typeof getTransition>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTransition>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetTransitionQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update a transition
 */
export const getUpdateTransitionUrl = (id: number) => {
  return `/api/transitions/${id}`;
};

export const updateTransition = async (
  id: number,
  updateTransitionBody: UpdateTransitionBody,
  options?: RequestInit,
): Promise<Transition> => {
  return customFetch<Transition>(getUpdateTransitionUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateTransitionBody),
  });
};

export const getUpdateTransitionMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTransition>>,
    TError,
    { id: number; data: BodyType<UpdateTransitionBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateTransition>>,
  TError,
  { id: number; data: BodyType<UpdateTransitionBody> },
  TContext
> => {
  const mutationKey = ["updateTransition"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateTransition>>,
    { id: number; data: BodyType<UpdateTransitionBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateTransition(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTransitionMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateTransition>>
>;
export type UpdateTransitionMutationBody = BodyType<UpdateTransitionBody>;
export type UpdateTransitionMutationError = ErrorType<unknown>;

/**
 * @summary Update a transition
 */
export const useUpdateTransition = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTransition>>,
    TError,
    { id: number; data: BodyType<UpdateTransitionBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateTransition>>,
  TError,
  { id: number; data: BodyType<UpdateTransitionBody> },
  TContext
> => {
  return useMutation(getUpdateTransitionMutationOptions(options));
};

/**
 * @summary Delete a transition
 */
export const getDeleteTransitionUrl = (id: number) => {
  return `/api/transitions/${id}`;
};

export const deleteTransition = async (
  id: number,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteTransitionUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteTransitionMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTransition>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTransition>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["deleteTransition"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTransition>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return deleteTransition(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTransitionMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTransition>>
>;

export type DeleteTransitionMutationError = ErrorType<unknown>;

/**
 * @summary Delete a transition
 */
export const useDeleteTransition = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTransition>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteTransition>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getDeleteTransitionMutationOptions(options));
};

/**
 * @summary List knowledge units for a transition
 */
export const getListKnowledgeUnitsUrl = (
  id: number,
  params?: ListKnowledgeUnitsParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/transitions/${id}/knowledge-units?${stringifiedParams}`
    : `/api/transitions/${id}/knowledge-units`;
};

export const listKnowledgeUnits = async (
  id: number,
  params?: ListKnowledgeUnitsParams,
  options?: RequestInit,
): Promise<KnowledgeUnit[]> => {
  return customFetch<KnowledgeUnit[]>(getListKnowledgeUnitsUrl(id, params), {
    ...options,
    method: "GET",
  });
};

export const getListKnowledgeUnitsQueryKey = (
  id: number,
  params?: ListKnowledgeUnitsParams,
) => {
  return [
    `/api/transitions/${id}/knowledge-units`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListKnowledgeUnitsQueryOptions = <
  TData = Awaited<ReturnType<typeof listKnowledgeUnits>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  params?: ListKnowledgeUnitsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listKnowledgeUnits>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListKnowledgeUnitsQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listKnowledgeUnits>>
  > = ({ signal }) =>
    listKnowledgeUnits(id, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listKnowledgeUnits>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListKnowledgeUnitsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listKnowledgeUnits>>
>;
export type ListKnowledgeUnitsQueryError = ErrorType<unknown>;

/**
 * @summary List knowledge units for a transition
 */

export function useListKnowledgeUnits<
  TData = Awaited<ReturnType<typeof listKnowledgeUnits>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  params?: ListKnowledgeUnitsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listKnowledgeUnits>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListKnowledgeUnitsQueryOptions(id, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Add a knowledge unit
 */
export const getCreateKnowledgeUnitUrl = (id: number) => {
  return `/api/transitions/${id}/knowledge-units`;
};

export const createKnowledgeUnit = async (
  id: number,
  createKnowledgeUnitBody: CreateKnowledgeUnitBody,
  options?: RequestInit,
): Promise<KnowledgeUnit> => {
  return customFetch<KnowledgeUnit>(getCreateKnowledgeUnitUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createKnowledgeUnitBody),
  });
};

export const getCreateKnowledgeUnitMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createKnowledgeUnit>>,
    TError,
    { id: number; data: BodyType<CreateKnowledgeUnitBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createKnowledgeUnit>>,
  TError,
  { id: number; data: BodyType<CreateKnowledgeUnitBody> },
  TContext
> => {
  const mutationKey = ["createKnowledgeUnit"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createKnowledgeUnit>>,
    { id: number; data: BodyType<CreateKnowledgeUnitBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return createKnowledgeUnit(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateKnowledgeUnitMutationResult = NonNullable<
  Awaited<ReturnType<typeof createKnowledgeUnit>>
>;
export type CreateKnowledgeUnitMutationBody = BodyType<CreateKnowledgeUnitBody>;
export type CreateKnowledgeUnitMutationError = ErrorType<unknown>;

/**
 * @summary Add a knowledge unit
 */
export const useCreateKnowledgeUnit = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createKnowledgeUnit>>,
    TError,
    { id: number; data: BodyType<CreateKnowledgeUnitBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createKnowledgeUnit>>,
  TError,
  { id: number; data: BodyType<CreateKnowledgeUnitBody> },
  TContext
> => {
  return useMutation(getCreateKnowledgeUnitMutationOptions(options));
};

/**
 * @summary List onboarding tasks for a transition
 */
export const getListOnboardingTasksUrl = (id: number) => {
  return `/api/transitions/${id}/onboarding-tasks`;
};

export const listOnboardingTasks = async (
  id: number,
  options?: RequestInit,
): Promise<OnboardingTask[]> => {
  return customFetch<OnboardingTask[]>(getListOnboardingTasksUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getListOnboardingTasksQueryKey = (id: number) => {
  return [`/api/transitions/${id}/onboarding-tasks`] as const;
};

export const getListOnboardingTasksQueryOptions = <
  TData = Awaited<ReturnType<typeof listOnboardingTasks>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listOnboardingTasks>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListOnboardingTasksQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listOnboardingTasks>>
  > = ({ signal }) => listOnboardingTasks(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listOnboardingTasks>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListOnboardingTasksQueryResult = NonNullable<
  Awaited<ReturnType<typeof listOnboardingTasks>>
>;
export type ListOnboardingTasksQueryError = ErrorType<unknown>;

/**
 * @summary List onboarding tasks for a transition
 */

export function useListOnboardingTasks<
  TData = Awaited<ReturnType<typeof listOnboardingTasks>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listOnboardingTasks>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListOnboardingTasksQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Add an onboarding task
 */
export const getCreateOnboardingTaskUrl = (id: number) => {
  return `/api/transitions/${id}/onboarding-tasks`;
};

export const createOnboardingTask = async (
  id: number,
  createOnboardingTaskBody: CreateOnboardingTaskBody,
  options?: RequestInit,
): Promise<OnboardingTask> => {
  return customFetch<OnboardingTask>(getCreateOnboardingTaskUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createOnboardingTaskBody),
  });
};

export const getCreateOnboardingTaskMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOnboardingTask>>,
    TError,
    { id: number; data: BodyType<CreateOnboardingTaskBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createOnboardingTask>>,
  TError,
  { id: number; data: BodyType<CreateOnboardingTaskBody> },
  TContext
> => {
  const mutationKey = ["createOnboardingTask"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createOnboardingTask>>,
    { id: number; data: BodyType<CreateOnboardingTaskBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return createOnboardingTask(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOnboardingTaskMutationResult = NonNullable<
  Awaited<ReturnType<typeof createOnboardingTask>>
>;
export type CreateOnboardingTaskMutationBody =
  BodyType<CreateOnboardingTaskBody>;
export type CreateOnboardingTaskMutationError = ErrorType<unknown>;

/**
 * @summary Add an onboarding task
 */
export const useCreateOnboardingTask = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOnboardingTask>>,
    TError,
    { id: number; data: BodyType<CreateOnboardingTaskBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createOnboardingTask>>,
  TError,
  { id: number; data: BodyType<CreateOnboardingTaskBody> },
  TContext
> => {
  return useMutation(getCreateOnboardingTaskMutationOptions(options));
};

/**
 * @summary Update an onboarding task (e.g. mark complete)
 */
export const getUpdateOnboardingTaskUrl = (taskId: number) => {
  return `/api/onboarding-tasks/${taskId}`;
};

export const updateOnboardingTask = async (
  taskId: number,
  updateOnboardingTaskBody: UpdateOnboardingTaskBody,
  options?: RequestInit,
): Promise<OnboardingTask> => {
  return customFetch<OnboardingTask>(getUpdateOnboardingTaskUrl(taskId), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateOnboardingTaskBody),
  });
};

export const getUpdateOnboardingTaskMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOnboardingTask>>,
    TError,
    { taskId: number; data: BodyType<UpdateOnboardingTaskBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateOnboardingTask>>,
  TError,
  { taskId: number; data: BodyType<UpdateOnboardingTaskBody> },
  TContext
> => {
  const mutationKey = ["updateOnboardingTask"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateOnboardingTask>>,
    { taskId: number; data: BodyType<UpdateOnboardingTaskBody> }
  > = (props) => {
    const { taskId, data } = props ?? {};

    return updateOnboardingTask(taskId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateOnboardingTaskMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateOnboardingTask>>
>;
export type UpdateOnboardingTaskMutationBody =
  BodyType<UpdateOnboardingTaskBody>;
export type UpdateOnboardingTaskMutationError = ErrorType<unknown>;

/**
 * @summary Update an onboarding task (e.g. mark complete)
 */
export const useUpdateOnboardingTask = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOnboardingTask>>,
    TError,
    { taskId: number; data: BodyType<UpdateOnboardingTaskBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateOnboardingTask>>,
  TError,
  { taskId: number; data: BodyType<UpdateOnboardingTaskBody> },
  TContext
> => {
  return useMutation(getUpdateOnboardingTaskMutationOptions(options));
};

/**
 * @summary Get dashboard summary stats
 */
export const getGetDashboardSummaryUrl = () => {
  return `/api/dashboard/summary`;
};

export const getDashboardSummary = async (
  options?: RequestInit,
): Promise<DashboardSummary> => {
  return customFetch<DashboardSummary>(getGetDashboardSummaryUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetDashboardSummaryQueryKey = () => {
  return [`/api/dashboard/summary`] as const;
};

export const getGetDashboardSummaryQueryOptions = <
  TData = Awaited<ReturnType<typeof getDashboardSummary>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getDashboardSummary>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDashboardSummaryQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getDashboardSummary>>
  > = ({ signal }) => getDashboardSummary({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getDashboardSummary>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetDashboardSummaryQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDashboardSummary>>
>;
export type GetDashboardSummaryQueryError = ErrorType<unknown>;

/**
 * @summary Get dashboard summary stats
 */

export function useGetDashboardSummary<
  TData = Awaited<ReturnType<typeof getDashboardSummary>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getDashboardSummary>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetDashboardSummaryQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get recent activity feed
 */
export const getGetRecentActivityUrl = () => {
  return `/api/dashboard/activity`;
};

export const getRecentActivity = async (
  options?: RequestInit,
): Promise<ActivityItem[]> => {
  return customFetch<ActivityItem[]>(getGetRecentActivityUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetRecentActivityQueryKey = () => {
  return [`/api/dashboard/activity`] as const;
};

export const getGetRecentActivityQueryOptions = <
  TData = Awaited<ReturnType<typeof getRecentActivity>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getRecentActivity>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRecentActivityQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getRecentActivity>>
  > = ({ signal }) => getRecentActivity({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getRecentActivity>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetRecentActivityQueryResult = NonNullable<
  Awaited<ReturnType<typeof getRecentActivity>>
>;
export type GetRecentActivityQueryError = ErrorType<unknown>;

/**
 * @summary Get recent activity feed
 */

export function useGetRecentActivity<
  TData = Awaited<ReturnType<typeof getRecentActivity>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getRecentActivity>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetRecentActivityQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Breakdown of transitions by phase
 */
export const getGetPhaseBreakdownUrl = () => {
  return `/api/dashboard/phase-breakdown`;
};

export const getPhaseBreakdown = async (
  options?: RequestInit,
): Promise<PhaseCount[]> => {
  return customFetch<PhaseCount[]>(getGetPhaseBreakdownUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetPhaseBreakdownQueryKey = () => {
  return [`/api/dashboard/phase-breakdown`] as const;
};

export const getGetPhaseBreakdownQueryOptions = <
  TData = Awaited<ReturnType<typeof getPhaseBreakdown>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getPhaseBreakdown>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPhaseBreakdownQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPhaseBreakdown>>
  > = ({ signal }) => getPhaseBreakdown({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPhaseBreakdown>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetPhaseBreakdownQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPhaseBreakdown>>
>;
export type GetPhaseBreakdownQueryError = ErrorType<unknown>;

/**
 * @summary Breakdown of transitions by phase
 */

export function useGetPhaseBreakdown<
  TData = Awaited<ReturnType<typeof getPhaseBreakdown>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getPhaseBreakdown>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetPhaseBreakdownQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Ask a question about this transition's captured knowledge
 */
export const getChatWithHandoverUrl = (id: number) => {
  return `/api/transitions/${id}/chat`;
};

export const chatWithHandover = async (
  id: number,
  chatWithHandoverBody: ChatWithHandoverBody,
  options?: RequestInit,
): Promise<ChatWithHandoverResponse> => {
  return customFetch<ChatWithHandoverResponse>(getChatWithHandoverUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(chatWithHandoverBody),
  });
};

export const getChatWithHandoverMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof chatWithHandover>>,
    TError,
    { id: number; data: BodyType<ChatWithHandoverBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof chatWithHandover>>,
  TError,
  { id: number; data: BodyType<ChatWithHandoverBody> },
  TContext
> => {
  const mutationKey = ["chatWithHandover"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof chatWithHandover>>,
    { id: number; data: BodyType<ChatWithHandoverBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return chatWithHandover(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ChatWithHandoverMutationResult = NonNullable<
  Awaited<ReturnType<typeof chatWithHandover>>
>;
export type ChatWithHandoverMutationBody = BodyType<ChatWithHandoverBody>;
export type ChatWithHandoverMutationError = ErrorType<void>;

/**
 * @summary Ask a question about this transition's captured knowledge
 */
export const useChatWithHandover = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof chatWithHandover>>,
    TError,
    { id: number; data: BodyType<ChatWithHandoverBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof chatWithHandover>>,
  TError,
  { id: number; data: BodyType<ChatWithHandoverBody> },
  TContext
> => {
  return useMutation(getChatWithHandoverMutationOptions(options));
};

/**
 * @summary AI suggests follow-up questions for the current interview block
 */
export const getSuggestInterviewFollowupsUrl = (id: number) => {
  return `/api/transitions/${id}/interview/suggest`;
};

export const suggestInterviewFollowups = async (
  id: number,
  interviewSuggestBody: InterviewSuggestBody,
  options?: RequestInit,
): Promise<InterviewSuggestResponse> => {
  return customFetch<InterviewSuggestResponse>(
    getSuggestInterviewFollowupsUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(interviewSuggestBody),
    },
  );
};

export const getSuggestInterviewFollowupsMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof suggestInterviewFollowups>>,
    TError,
    { id: number; data: BodyType<InterviewSuggestBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof suggestInterviewFollowups>>,
  TError,
  { id: number; data: BodyType<InterviewSuggestBody> },
  TContext
> => {
  const mutationKey = ["suggestInterviewFollowups"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof suggestInterviewFollowups>>,
    { id: number; data: BodyType<InterviewSuggestBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return suggestInterviewFollowups(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SuggestInterviewFollowupsMutationResult = NonNullable<
  Awaited<ReturnType<typeof suggestInterviewFollowups>>
>;
export type SuggestInterviewFollowupsMutationBody =
  BodyType<InterviewSuggestBody>;
export type SuggestInterviewFollowupsMutationError = ErrorType<unknown>;

/**
 * @summary AI suggests follow-up questions for the current interview block
 */
export const useSuggestInterviewFollowups = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof suggestInterviewFollowups>>,
    TError,
    { id: number; data: BodyType<InterviewSuggestBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof suggestInterviewFollowups>>,
  TError,
  { id: number; data: BodyType<InterviewSuggestBody> },
  TContext
> => {
  return useMutation(getSuggestInterviewFollowupsMutationOptions(options));
};

/**
 * @summary AI extracts draft Decision Units from a transcript block
 */
export const getExtractDecisionUnitsUrl = (id: number) => {
  return `/api/transitions/${id}/interview/extract`;
};

export const extractDecisionUnits = async (
  id: number,
  interviewExtractBody: InterviewExtractBody,
  options?: RequestInit,
): Promise<InterviewExtractResponse> => {
  return customFetch<InterviewExtractResponse>(getExtractDecisionUnitsUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(interviewExtractBody),
  });
};

export const getExtractDecisionUnitsMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof extractDecisionUnits>>,
    TError,
    { id: number; data: BodyType<InterviewExtractBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof extractDecisionUnits>>,
  TError,
  { id: number; data: BodyType<InterviewExtractBody> },
  TContext
> => {
  const mutationKey = ["extractDecisionUnits"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof extractDecisionUnits>>,
    { id: number; data: BodyType<InterviewExtractBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return extractDecisionUnits(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ExtractDecisionUnitsMutationResult = NonNullable<
  Awaited<ReturnType<typeof extractDecisionUnits>>
>;
export type ExtractDecisionUnitsMutationBody = BodyType<InterviewExtractBody>;
export type ExtractDecisionUnitsMutationError = ErrorType<unknown>;

/**
 * @summary AI extracts draft Decision Units from a transcript block
 */
export const useExtractDecisionUnits = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof extractDecisionUnits>>,
    TError,
    { id: number; data: BodyType<InterviewExtractBody> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof extractDecisionUnits>>,
  TError,
  { id: number; data: BodyType<InterviewExtractBody> },
  TContext
> => {
  return useMutation(getExtractDecisionUnitsMutationOptions(options));
};

/**
 * @summary Self-progress metric for the person in transition (Reputation System)
 */
export const getGetExpertiseSignalUrl = (id: number) => {
  return `/api/transitions/${id}/expertise-signal`;
};

export const getExpertiseSignal = async (
  id: number,
  options?: RequestInit,
): Promise<ExpertiseSignal> => {
  return customFetch<ExpertiseSignal>(getGetExpertiseSignalUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetExpertiseSignalQueryKey = (id: number) => {
  return [`/api/transitions/${id}/expertise-signal`] as const;
};

export const getGetExpertiseSignalQueryOptions = <
  TData = Awaited<ReturnType<typeof getExpertiseSignal>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getExpertiseSignal>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetExpertiseSignalQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getExpertiseSignal>>
  > = ({ signal }) => getExpertiseSignal(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getExpertiseSignal>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetExpertiseSignalQueryResult = NonNullable<
  Awaited<ReturnType<typeof getExpertiseSignal>>
>;
export type GetExpertiseSignalQueryError = ErrorType<unknown>;

/**
 * @summary Self-progress metric for the person in transition (Reputation System)
 */

export function useGetExpertiseSignal<
  TData = Awaited<ReturnType<typeof getExpertiseSignal>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getExpertiseSignal>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetExpertiseSignalQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Generate a per-phase deliverable for the person in transition
 */
export const getGetDeliverableUrl = (
  id: number,
  kind:
    | "self_insight"
    | "professional_identity"
    | "knowledge_signature"
    | "opportunity_layer"
    | "legacy_report",
) => {
  return `/api/transitions/${id}/deliverables/${kind}`;
};

export const getDeliverable = async (
  id: number,
  kind:
    | "self_insight"
    | "professional_identity"
    | "knowledge_signature"
    | "opportunity_layer"
    | "legacy_report",
  options?: RequestInit,
): Promise<Deliverable> => {
  return customFetch<Deliverable>(getGetDeliverableUrl(id, kind), {
    ...options,
    method: "GET",
  });
};

export const getGetDeliverableQueryKey = (
  id: number,
  kind:
    | "self_insight"
    | "professional_identity"
    | "knowledge_signature"
    | "opportunity_layer"
    | "legacy_report",
) => {
  return [`/api/transitions/${id}/deliverables/${kind}`] as const;
};

export const getGetDeliverableQueryOptions = <
  TData = Awaited<ReturnType<typeof getDeliverable>>,
  TError = ErrorType<void>,
>(
  id: number,
  kind:
    | "self_insight"
    | "professional_identity"
    | "knowledge_signature"
    | "opportunity_layer"
    | "legacy_report",
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getDeliverable>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetDeliverableQueryKey(id, kind);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDeliverable>>> = ({
    signal,
  }) => getDeliverable(id, kind, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!(id && kind),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getDeliverable>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetDeliverableQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDeliverable>>
>;
export type GetDeliverableQueryError = ErrorType<void>;

/**
 * @summary Generate a per-phase deliverable for the person in transition
 */

export function useGetDeliverable<
  TData = Awaited<ReturnType<typeof getDeliverable>>,
  TError = ErrorType<void>,
>(
  id: number,
  kind:
    | "self_insight"
    | "professional_identity"
    | "knowledge_signature"
    | "opportunity_layer"
    | "legacy_report",
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getDeliverable>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetDeliverableQueryOptions(id, kind, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Calculate ROI for given inputs
 */
export const getCalculateRoiUrl = () => {
  return `/api/roi-calculator`;
};

export const calculateRoi = async (
  roiCalculatorInput: RoiCalculatorInput,
  options?: RequestInit,
): Promise<RoiCalculatorResult> => {
  return customFetch<RoiCalculatorResult>(getCalculateRoiUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(roiCalculatorInput),
  });
};

export const getCalculateRoiMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof calculateRoi>>,
    TError,
    { data: BodyType<RoiCalculatorInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof calculateRoi>>,
  TError,
  { data: BodyType<RoiCalculatorInput> },
  TContext
> => {
  const mutationKey = ["calculateRoi"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof calculateRoi>>,
    { data: BodyType<RoiCalculatorInput> }
  > = (props) => {
    const { data } = props ?? {};

    return calculateRoi(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CalculateRoiMutationResult = NonNullable<
  Awaited<ReturnType<typeof calculateRoi>>
>;
export type CalculateRoiMutationBody = BodyType<RoiCalculatorInput>;
export type CalculateRoiMutationError = ErrorType<unknown>;

/**
 * @summary Calculate ROI for given inputs
 */
export const useCalculateRoi = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof calculateRoi>>,
    TError,
    { data: BodyType<RoiCalculatorInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof calculateRoi>>,
  TError,
  { data: BodyType<RoiCalculatorInput> },
  TContext
> => {
  return useMutation(getCalculateRoiMutationOptions(options));
};
